import PropTypes from "prop-types";
import * as React from "react";
import { useDebounce } from "../../../hooks";
import { useCurrentUser } from "../../../globalState";
import { Button, Dialog, TextInput, CheckIcon } from "../../../themeV2";
import css from "./AdvancedSettings.module.css";
import { useUserInfluencers, useUserSearch } from "./hooks";
import { addUserAccess, updateUserInfluencer } from "./service";

export default function ChangeShopOwner({
  influencerId,
  triggerAlert,
  setErrorText,
}) {
  const currentUser = useCurrentUser();
  const { userInfluencers, refresh } = useUserInfluencers(influencerId);
  const currentOwner = React.useMemo(
    () => userInfluencers.find((u) => u.is_owner),
    [userInfluencers],
  );

  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [confirmUser, setConfirmUser] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  useDebounce(() => setDebouncedQuery(searchQuery), 500, [searchQuery]);

  const { users, isLoading: isSearching } = useUserSearch(debouncedQuery);

  const handleConfirm = async () => {
    if (!confirmUser || isSubmitting) return;
    setIsSubmitting(true);
    setErrorText("");

    try {
      let accessRecord = userInfluencers.find(
        (u) => u.user_id === confirmUser.user_id,
      );

      if (!accessRecord) {
        await addUserAccess(influencerId, confirmUser.user_id);
        const refreshed = await refresh();
        accessRecord = refreshed?.user_influencers?.find(
          (u) => u.user_id === confirmUser.user_id,
        );
      }

      await updateUserInfluencer(influencerId, {
        ...accessRecord,
        is_owner: true,
      });

      if (currentOwner && currentOwner.user_id !== confirmUser.user_id) {
        await updateUserInfluencer(influencerId, {
          ...currentOwner,
          is_owner: false,
        });
      }

      refresh();
      triggerAlert({ message: "Shop owner updated" });
      setErrorText("");
      setSearchOpen(false);
      setSearchQuery("");
      setDebouncedQuery("");
      setConfirmUser(null);
    } catch (err) {
      console.error(err);
      refresh();
      setConfirmUser(null);
      setErrorText(err.message || "Failed to change shop owner");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <p className={css.caption}>
        Details such as shipping address, key roles and social handles rely on
        this account.
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          marginBlockStart: "var(--space-4)",
        }}
      >
        {currentOwner ? (
          <div
            style={{
              display: "flex",
              gap: "var(--space-2)",
              alignItems: "center",
            }}
          >
            <span
              style={{
                font: "var(--type-body-lg)",
                letterSpacing: "var(--tracking-body-lg)",
              }}
            >
              {currentOwner.email}
            </span>
            <CheckIcon
              style={{ width: 20, height: 20, color: "var(--status-success)" }}
            />
          </div>
        ) : (
          <span className={css.caption}>No owner assigned</span>
        )}
      </div>

      {currentUser.isAdmin && (
        <div style={{ marginBlockStart: "var(--space-4)" }}>
          <Button
            variant="outlined"
            onClick={() => {
              setErrorText("");
              setSearchOpen((prev) => !prev);
            }}
          >
            {searchOpen ? "Cancel" : "Change Owner"}
          </Button>
        </div>
      )}

      {searchOpen && (
        <div className={css.searchPanel}>
          <TextInput
            label="Search by email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            description="Type at least 4 characters to search"
            autoFocus
          />

          {isSearching && <p className={css.caption}>Searching...</p>}

          {users.length > 0 && (
            <ul className={css.resultList}>
              {users.map((user) => (
                <li
                  key={user.user_id}
                  className={css.resultItem}
                  role="button"
                  tabIndex={0}
                  onClick={() => setConfirmUser(user)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setConfirmUser(user);
                    }
                  }}
                >
                  {user.email}
                </li>
              ))}
            </ul>
          )}

          {debouncedQuery.length >= 4 && !isSearching && users.length === 0 && (
            <p className={css.caption}>No users found</p>
          )}
        </div>
      )}

      <Dialog.Root
        alert
        open={Boolean(confirmUser)}
        onOpenChange={(open) => {
          if (!open) setConfirmUser(null);
        }}
      >
        <Dialog.Popup size="sm">
          <Dialog.Title>Change shop owner</Dialog.Title>
          <Dialog.Description>
            Are you sure you want to set {confirmUser?.email} as the shop owner?
          </Dialog.Description>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "var(--space-2)",
              marginBlockStart: "var(--space-4)",
            }}
          >
            <Button variant="outlined" onClick={() => setConfirmUser(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </div>
        </Dialog.Popup>
      </Dialog.Root>
    </div>
  );
}

ChangeShopOwner.propTypes = {
  influencerId: PropTypes.number.isRequired,
  triggerAlert: PropTypes.func.isRequired,
  setErrorText: PropTypes.func.isRequired,
};
